import { get, has, set, startCase } from "lodash-es";

import { type BuilderFlags, type UserInfo } from "helpers/AppProvider.types";

import { type InternalUserRoleDetailsEntity } from "entity/internalUser/InternalUserRoleDetails/InternalUserRoleDetails.api.types";
import {
    type IRolePermissionFormValues,
    type Permission,
    type Role,
} from "entity/internalUser/RolePermissions/RolePermissions.api.types";

const enable = (
    values: IRolePermissionFormValues,
    targetPath: string,
    shouldSet: boolean,
    isReadOnly?: boolean,
    readOnlyMessage?: string,
) => setIf(shouldSet, values, targetPath, true, isReadOnly, readOnlyMessage);

const disable = (
    values: IRolePermissionFormValues,
    targetPath: string,
    shouldSet: boolean,
    isReadOnly?: boolean,
    readOnlyMessage?: string,
) => setIf(shouldSet, values, targetPath, false, isReadOnly, readOnlyMessage);

export const ifAll = (
    values: IRolePermissionFormValues,
    dependencyPaths: string[] | IPermissionDependencyTest[],
): boolean => {
    if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "string") {
        return (
            get(values, getFullPath(dependencyPaths[0])) && ifAll(values, dependencyPaths.slice(1))
        );
    } else if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "object") {
        return (
            dependencyPaths[0].test(values, dependencyPaths[0].dependencies) &&
            ifAll(values, dependencyPaths.slice(1))
        );
    }
    return true;
};
export const ifAny = (
    values: IRolePermissionFormValues,
    dependencyPaths: string[] | IPermissionDependencyTest[],
): boolean => {
    if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "string") {
        return (
            get(values, getFullPath(dependencyPaths[0])) || ifAny(values, dependencyPaths.slice(1))
        );
    } else if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "object") {
        return (
            dependencyPaths[0].test(values, dependencyPaths[0].dependencies) ||
            ifAny(values, dependencyPaths.slice(1))
        );
    }
    return false;
};
export const ifNone = (
    values: IRolePermissionFormValues,
    dependencyPaths: string[] | IPermissionDependencyTest[],
): boolean => {
    if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "string") {
        return (
            !get(values, getFullPath(dependencyPaths[0])) &&
            ifNone(values, dependencyPaths.slice(1))
        );
    } else if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "object") {
        return (
            !dependencyPaths[0].test(values, dependencyPaths[0].dependencies) &&
            ifNone(values, dependencyPaths.slice(1))
        );
    }
    return true;
};
export const ifNotAll = (
    values: IRolePermissionFormValues,
    dependencyPaths: string[] | IPermissionDependencyTest[],
): boolean => {
    if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "string") {
        return (
            !get(values, getFullPath(dependencyPaths[0])) ||
            ifNotAll(values, dependencyPaths.slice(1))
        );
    } else if (dependencyPaths.length > 0 && typeof dependencyPaths[0] === "object") {
        return (
            !dependencyPaths[0].test(values, dependencyPaths[0].dependencies) ||
            ifNotAll(values, dependencyPaths.slice(1))
        );
    }
    return false;
};

function setIf(
    shouldSet: boolean,
    values: IRolePermissionFormValues,
    targetPath: string,
    to: boolean,
    isReadOnly?: boolean,
    readOnlyMessage?: string,
): void {
    if (shouldSet) {
        set(values, `${targetPath}.enabled`, to);

        if (isReadOnly !== undefined) {
            set(values, `${targetPath}.readOnly`, isReadOnly);

            if (readOnlyMessage !== undefined) {
                set(values, `${targetPath}.message`, readOnlyMessage);
            }
        }
    }
}

/** Return path for permissions on the sub-category level */
export const getPermissionFormPath = (
    categoryId: string,
    subCategory: string,
    permission: string,
): string => {
    return `categories.${categoryId}.subCategories.${subCategory}.permissions.${permission}`;
};

function getFullPath(simplePath: string): string {
    const parts = simplePath.split(".");

    let statePart = "";
    let permissionPath = "";
    if (parts[parts.length - 1] === "enabled" || parts[parts.length - 1] === "readOnly") {
        statePart = parts.pop()!;
    }

    if (parts.length === 3) {
        // Subcategory permission format: category.subCategory.permission[.state]
        const permissionParts = parts.slice(0, 3);

        const category = permissionParts[0];
        const subCategory = permissionParts[1];
        const permission = permissionParts[2];

        permissionPath = getPermissionFormPath(category, subCategory, permission);
    } else if (parts.length === 2) {
        // Category permission format: category.permission[.state]
        const category = parts[0];
        const permission = parts[1];
        permissionPath = getCategoryPermissionFormPath(category, permission);
    } else {
        // Fallback for invalid paths
        permissionPath = simplePath;
    }

    if (statePart) {
        permissionPath = `${permissionPath}.${statePart}`;
    }

    return permissionPath;
}

interface ITestable {
    test: (
        values: IRolePermissionFormValues,
        dependencies: string[] | IPermissionDependencyTest[],
    ) => boolean;
}
interface IPermissionDependencyTest extends ITestable {
    /**
     * The paths for this dependency. These will be checked against the test predicate.
     */
    dependencies: string[];
}

// Handles complex conditionals (eg. (A && B) || (C && D))
interface IPermissionDependencyTestSet extends ITestable {
    dependencies: IPermissionDependencyTest[];
}

// The previous approach actively changes individual permissions when one is changed.
// This approach will check dependencies for each permission instead based on form state.
export interface IPermissionDependency {
    permissionPath: string;
    /**
     * A list of tests that prevent a permission from being turned on unless they pass.
     */
    requires?: IPermissionDependencyTest | IPermissionDependencyTestSet;
    /**
     * A list of tests that will automatically enable this permission if they pass.
     */
    isEnabled?: IPermissionDependencyTest | IPermissionDependencyTestSet;
    /**
     * A list of tests that will set this permission's state to read-only if they pass.
     */
    isReadOnly?: IPermissionDependencyTest | IPermissionDependencyTestSet;
}

// When this is used as a list, order matters.
// This can later be used to build a depency tree to remove that issue.
const clientUpdateDependencies: IPermissionDependency[] = [
    {
        permissionPath: "projectManagement.clientUpdatePerms.view",
        isEnabled: {
            test: ifAny,
            dependencies: [
                "projectManagement.clientUpdatePerms.add.enabled",
                "projectManagement.clientUpdatePerms.update.enabled",
                "projectManagement.clientUpdatePerms.delete.enabled",
            ],
        },
    },
    {
        permissionPath: "projectManagement.clientUpdatePerms.add",
        requires: {
            test: ifAll,
            dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
        },
        isEnabled: {
            test: ifAny,
            dependencies: [
                "projectManagement.clientUpdatePerms.update.enabled",
                "projectManagement.clientUpdatePerms.delete.enabled",
            ],
        },
    },
    {
        permissionPath: "projectManagement.clientUpdatePerms.update",
        requires: {
            test: ifAll,
            dependencies: ["projectManagement.clientUpdatePerms.add.enabled"],
        },
        isEnabled: {
            test: ifAny,
            dependencies: ["projectManagement.clientUpdatePerms.delete.enabled"],
        },
    },
    {
        permissionPath: "projectManagement.clientUpdatePerms.delete",
        requires: {
            test: ifAll,
            dependencies: ["projectManagement.clientUpdatePerms.update.enabled"],
        },
    },
];

function createSalesDependencies(): IPermissionDependency[] {
    return [
        ...addVAEDHierarchyDeps("sales", "leadPerms"),
        {
            permissionPath: "administrative.contactPerms.view",
            isEnabled: {
                test: ifAny,
                dependencies: [
                    "sales.leadPerms.view.enabled",
                    "administrative.jobsitePerms.view.enabled",
                ],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    "sales.leadPerms.view.enabled",
                    "administrative.jobsitePerms.view.enabled",
                ],
            },
        },
        {
            permissionPath: "administrative.contactPerms.add",
            isEnabled: {
                test: ifAny,
                dependencies: [
                    "sales.leadPerms.add.enabled",
                    "administrative.jobsitePerms.add.enabled",
                ],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    "sales.leadPerms.add.enabled",
                    "administrative.jobsitePerms.add.enabled",
                ],
            },
        },
    ];
}

function createFilesDependencies(): IPermissionDependency[] {
    return [
        ...addVAEDHierarchyDeps("files", "documentPerms"),
        ...addVAEDHierarchyDeps("files", "videoPerms"),
        ...addVAEDHierarchyDeps("files", "photoPerms"),
        {
            permissionPath: "files.documentPerms.pdfSignatureRequest",
            isReadOnly: {
                test: ifNone,
                dependencies: ["files.documentPerms.update.enabled"],
            },
            requires: {
                test: ifAll,
                dependencies: ["files.documentPerms.update.enabled"],
            },
        },
    ];
}

function createMessagingDependencies(): IPermissionDependency[] {
    return [
        ...addVAEDHierarchyDeps("messaging", "rfiPerms"),
        ...addVAEDHierarchyDeps("messaging", "surveyPerms"),
        ...addVAEDHierarchyDeps("messaging", "emailPerms"),
        ...addVAEDHierarchyDeps("messaging", "chatPerms"),
        {
            permissionPath: "messaging.emailPerms.add",
            isEnabled: {
                test: ifAll,
                dependencies: ["messaging.emailPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: [],
            },
        },
        {
            permissionPath: "messaging.emailPerms.update",
            isEnabled: {
                test: ifAll,
                dependencies: ["messaging.emailPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: [],
            },
        },
    ];
}

function createAdministrativeDependencies(): IPermissionDependency[] {
    return [
        ...addVAEDHierarchyDeps("administrative", "jobsitePerms"),
        ...addVAEDHierarchyDeps("administrative", "subsPerms"),
        ...addVAEDHierarchyDeps("administrative", "contactPerms"),
        ...addVAEDHierarchyDeps("administrative", "adminPerms"),
        {
            permissionPath: "administrative.contactPerms.viewAllCustomerContacts",
            isEnabled: {
                test: ifAll,
                dependencies: [
                    "administrative.adminPerms.view.enabled",
                    "administrative.adminPerms.add.enabled",
                    "administrative.adminPerms.update.enabled",
                ],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: [
                    "administrative.adminPerms.view.enabled",
                    "administrative.adminPerms.add.enabled",
                    "administrative.adminPerms.update.enabled",
                ],
            },
        },
        {
            permissionPath: "administrative.adminPerms.showReportingBetaOption",
            isReadOnly: {
                test: ifNotAll,
                dependencies: [
                    "administrative.adminPerms.view.enabled",
                    "administrative.adminPerms.add.enabled",
                    "administrative.adminPerms.update.enabled",
                    "administrative.adminPerms.delete.enabled",
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    "administrative.adminPerms.view.enabled",
                    "administrative.adminPerms.add.enabled",
                    "administrative.adminPerms.update.enabled",
                    "administrative.adminPerms.delete.enabled",
                ],
            },
        },
        {
            permissionPath: "administrative.jobsitePerms.viewRequestRiskInsurance",
            isReadOnly: {
                test: ifNotAll,
                dependencies: [
                    {
                        test: ifAll,
                        dependencies: [
                            "priceViewing.jobsitesPriceViewing.enabled",
                            "administrative.jobsitePerms.view.enabled",
                        ],
                    },
                    {
                        test: ifAny,
                        dependencies: [
                            "administrative.jobsitePerms.add.enabled",
                            "administrative.jobsitePerms.update.enabled",
                        ],
                    },
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    {
                        test: ifAll,
                        dependencies: [
                            "priceViewing.jobsitesPriceViewing.enabled",
                            "administrative.jobsitePerms.view.enabled",
                        ],
                    },
                    {
                        test: ifAny,
                        dependencies: [
                            "administrative.jobsitePerms.add.enabled",
                            "administrative.jobsitePerms.update.enabled",
                        ],
                    },
                ],
            },
        },
    ];
}

const priceViewingDependencies: IPermissionDependency[] = [
    {
        permissionPath: "priceViewing.jobsitesPriceViewing",
        isEnabled: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
        isReadOnly: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
    },
    {
        permissionPath: "priceViewing.coPriceViewing",
        isEnabled: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
        isReadOnly: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
    },
    {
        permissionPath: "priceViewing.selectionPriceViewing",
        isEnabled: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
        isReadOnly: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
    },
    {
        permissionPath: "priceViewing.poPriceViewing",
        isEnabled: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
        isReadOnly: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
    },
    {
        permissionPath: "priceViewing.timeClockCostViewing",
        isEnabled: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
        isReadOnly: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
    },
    {
        permissionPath: "priceViewing.estimatePriceViewing",
        isEnabled: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
        isReadOnly: {
            test: ifAll,
            dependencies: ["financial.budgetPerms.view.enabled"],
        },
    },
];

function createTimeClockDependencies(): IPermissionDependency[] {
    return [
        ...addVAEDHierarchyDeps("projectManagement", "timeClockPerms"),
        {
            permissionPath: "projectManagement.timeClockPerms.viewOtherUsers",
            isEnabled: {
                test: ifAny,
                dependencies: [
                    "projectManagement.timeClockPerms.reviewShift.enabled",
                    "projectManagement.timeClockPerms.adjustOtherUsers.enabled",
                ],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    "projectManagement.timeClockPerms.reviewShift.enabled",
                    "projectManagement.timeClockPerms.adjustOtherUsers.enabled",
                ],
            },
        },
        {
            permissionPath: "projectManagement.timeClockPerms.reviewShift",
            isReadOnly: {
                test: ifNone,
                dependencies: ["projectManagement.timeClockPerms.update.enabled"],
            },
            requires: {
                test: ifAll,
                dependencies: ["projectManagement.timeClockPerms.update.enabled"],
            },
        },
        {
            permissionPath: "projectManagement.calendarPerms.resetBaseline",
            isReadOnly: {
                test: ifNone,
                dependencies: ["projectManagement.calendarPerms.update.enabled"],
            },
            requires: {
                test: ifAll,
                dependencies: ["projectManagement.calendarPerms.update.enabled"],
            },
        },
        {
            permissionPath: "projectManagement.timeClockPerms.view",
            isEnabled: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
        },
    ];
}

function createFinancialDependencies(): IPermissionDependency[] {
    return [
        ...addVAEDHierarchyDeps("financial", "biddingPerms"),
        ...addVAEDHierarchyDeps("financial", "proposalPerms"),
        ...addVAEDHierarchyDeps("financial", "estimatePerms"),
        ...addVAEDHierarchyDeps("financial", "budgetPerms"),
        ...addVAEDHierarchyDeps("financial", "purchaseOrderPerms"),
        ...addVAEDHierarchyDeps("financial", "receiptPerms"),
        ...addVAEDHierarchyDeps("financial", "paymentPerms"),
        ...addVAEDHierarchyDeps("financial", "payrollPerms"),
        {
            permissionPath: "financial.paymentPerms.view",
            isEnabled: {
                test: ifAny,
                dependencies: [
                    "financial.budgetPerms.view.enabled",
                    "projectManagement.clientUpdatePerms.view.enabled",
                ],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    "financial.budgetPerms.view.enabled",
                    "projectManagement.clientUpdatePerms.view.enabled",
                ],
            },
        },
        {
            permissionPath: "financial.paymentPerms.creditMemosCreateEdit",
            requires: {
                test: ifAny,
                dependencies: [
                    "financial.paymentPerms.view.enabled",
                    "financial.budgetPerms.view.enabled",
                ],
            },
        },
        {
            permissionPath: "financial.biddingPerms.view",
            isEnabled: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
        },
        {
            permissionPath: "financial.estimatePerms.view",
            isEnabled: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
        },
        {
            permissionPath: "financial.purchaseOrderPerms.view",
            isEnabled: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
        },
        {
            permissionPath: "financial.receiptPerms.view",
            isEnabled: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
        },
        {
            permissionPath: "financial.proposalPerms.view",
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    {
                        test: ifAll,
                        dependencies: ["financial.budgetPerms.view.enabled"],
                    },
                    {
                        test: ifNotAll,
                        dependencies: [
                            "financial.estimatePerms.view.enabled",
                            "priceViewing.estimatePriceViewing.enabled",
                        ],
                    },
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
            isEnabled: {
                test: ifAll,
                dependencies: ["financial.budgetPerms.view.enabled"],
            },
        },
        {
            permissionPath: "financial.proposalPerms.add",
            isReadOnly: {
                test: ifNotAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
        },
        {
            permissionPath: "financial.proposalPerms.update",
            isReadOnly: {
                test: ifNotAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
        },
        {
            permissionPath: "financial.proposalPerms.delete",
            isReadOnly: {
                test: ifNotAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    "financial.estimatePerms.view.enabled",
                    "priceViewing.estimatePriceViewing.enabled",
                ],
            },
        },
    ];
}

function createProjectManagementDependencies(builderFlags?: BuilderFlags): IPermissionDependency[] {
    const shouldTieToDosToClientUpdates = !builderFlags?.disableToDos;

    const todoDependency: IPermissionDependency | null = shouldTieToDosToClientUpdates
        ? {
              permissionPath: "projectManagement.toDoPerms.view",
              isEnabled: {
                  test: ifAny,
                  dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
              },
              isReadOnly: {
                  test: ifAny,
                  dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
              },
          }
        : null;

    const dependencies: IPermissionDependency[] = [
        ...addVAEDHierarchyDeps("projectManagement", "calendarPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "dailyLogPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "toDoPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "changeOrderPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "selectionPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "specificationsPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "warrantyPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "timeClockPerms"),
        ...addVAEDHierarchyDeps("projectManagement", "clientUpdatePerms"),
        {
            permissionPath: `projectManagement.taskPerms.view`,
            isEnabled: {
                test: ifAny,
                dependencies: [`projectManagement.taskPerms.update.enabled`],
            },
        },
        {
            permissionPath: `projectManagement.taskPerms.update`,
            requires: {
                test: ifAll,
                dependencies: [`projectManagement.taskPerms.view.enabled`],
            },
        },
        {
            permissionPath: "projectManagement.calendarPerms.view",
            isEnabled: {
                test: ifAny,
                dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
            },
        },
    ];

    if (todoDependency !== null) {
        dependencies.push(todoDependency);
    }

    dependencies.push(
        {
            permissionPath: "projectManagement.dailyLogPerms.view",
            isEnabled: {
                test: ifAny,
                dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: ["projectManagement.clientUpdatePerms.view.enabled"],
            },
        },
        {
            permissionPath: "projectManagement.selectionPerms.view",
            isEnabled: {
                test: ifAny,
                dependencies: [
                    "projectManagement.clientUpdatePerms.view.enabled",
                    "financial.budgetPerms.view.enabled",
                ],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    "projectManagement.clientUpdatePerms.view.enabled",
                    "financial.budgetPerms.view.enabled",
                ],
            },
        },
        {
            permissionPath: "projectManagement.changeOrderPerms.view",
            isEnabled: {
                test: ifAny,
                dependencies: [
                    "projectManagement.clientUpdatePerms.view.enabled",
                    "financial.budgetPerms.view.enabled",
                ],
            },
            isReadOnly: {
                test: ifAny,
                dependencies: [
                    "projectManagement.clientUpdatePerms.view.enabled",
                    "financial.budgetPerms.view.enabled",
                ],
            },
        },
    );

    return clientUpdateDependencies.concat(dependencies);
}

function getPermissionDependencies(builderFlags?: BuilderFlags): IPermissionDependency[] {
    return createProjectManagementDependencies(builderFlags)
        .concat(priceViewingDependencies)
        .concat(createFinancialDependencies())
        .concat(createSalesDependencies())
        .concat(createFilesDependencies())
        .concat(createMessagingDependencies())
        .concat(createAdministrativeDependencies())
        .concat(createTimeClockDependencies());
}

function formatPermissionName(path: string) {
    const parts = path.split(".");
    const permissionParts = parts.slice(0, 3);
    let subCategory = startCase(permissionParts[1].replace("Perms", ""));
    let permission = startCase(permissionParts[2]);

    subCategory = permissionFriendlyNames[subCategory] || subCategory;
    permission = permissionFriendlyNames[permission] || permission;

    if (permission === "Enabled" || permission === "") {
        return subCategory;
    }

    return `${subCategory} - ${permission}`;
}

/**
 * Creates standard View-Add-Edit-Delete (VAED) hierarchy permission dependencies
 * Enabling a higher permission will enable all lower permissions
 * Disabling a lower permission will disable all higher permissions
 *
 * @param categoryName - The category name (e.g. "sales")
 * @param permName - The permission name (e.g. "leadPerms")
 * @returns Array of permission dependencies following VAED hierarchy
 */
function addVAEDHierarchyDeps(categoryName: string, permName: string): IPermissionDependency[] {
    return [
        {
            permissionPath: `${categoryName}.${permName}.view`,
            isEnabled: {
                test: ifAny,
                dependencies: [
                    `${categoryName}.${permName}.add.enabled`,
                    `${categoryName}.${permName}.update.enabled`,
                    `${categoryName}.${permName}.delete.enabled`,
                ],
            },
        },
        {
            permissionPath: `${categoryName}.${permName}.add`,
            isEnabled: {
                test: ifAny,
                dependencies: [
                    `${categoryName}.${permName}.update.enabled`,
                    `${categoryName}.${permName}.delete.enabled`,
                ],
            },
            requires: {
                test: ifAll,
                dependencies: [`${categoryName}.${permName}.view.enabled`],
            },
        },
        {
            permissionPath: `${categoryName}.${permName}.update`,
            isEnabled: {
                test: ifAny,
                dependencies: [`${categoryName}.${permName}.delete.enabled`],
            },
            requires: {
                test: ifAll,
                dependencies: [
                    `${categoryName}.${permName}.view.enabled`,
                    `${categoryName}.${permName}.add.enabled`,
                ],
            },
        },
        {
            permissionPath: `${categoryName}.${permName}.delete`,
            requires: {
                test: ifAll,
                dependencies: [
                    `${categoryName}.${permName}.view.enabled`,
                    `${categoryName}.${permName}.add.enabled`,
                    `${categoryName}.${permName}.update.enabled`,
                ],
            },
        },
    ];
}

// Mappying from name in code to name displayed in the UX
const permissionFriendlyNames: { [key: string]: string } = {
    Budget: "Job costing budget",
    Jobsite: "Job Info",
    Estimate: "Estimate general items",
    "Jobsites Price Viewing": "Price viewing - Job Info",
    "Co Price Viewing": "Price viewing - Change Orders",
    "Selection Price Viewing": "Price viewing - Selections",
    "Po Price Viewing": "Price viewing - Bills/POs",
    "Time Clock Cost Viewing": "Price viewing - Time Clock",
    "Estimate Price Viewing": "Price viewing - Estimate general items",
    Calendar: "Schedule",
    "View Other Users": "View other user time sheets",
    "Review Shift": "Review and Approve user shifts",
    Email: "Messages",
    Contact: "Client Contacts",
    "View All Customer Contacts": "View all",
    View: "view",
    Add: "add",
    Update: "edit",
    Delete: "delete",
};

// When adding new dependencies, prefer using the IPermissionDependency structure above
// instead of handling them with individual functions unless custom or complicated behavior is required.
function verifyDependencyState(
    formValues: IRolePermissionFormValues,
    fieldId: string,
    builderFlags?: BuilderFlags,
): void {
    getPermissionDependencies(builderFlags).forEach((dependency) => {
        const { permissionPath, requires, isEnabled, isReadOnly } = dependency;
        const fullPath = getFullPath(permissionPath);

        if (!has(formValues, fullPath)) {
            return;
        }

        if (permissionPath.includes(fieldId)) {
            // Do not update the permission that was just changed
            return;
        }

        if (isEnabled !== undefined) {
            const shouldEnable = isEnabled.test(formValues, isEnabled.dependencies);
            enable(formValues, fullPath, shouldEnable);
        }
        if (requires) {
            const shouldDisable = !requires.test(formValues, requires.dependencies);
            disable(formValues, fullPath, shouldDisable);
        }

        if (isReadOnly !== undefined) {
            const readonly = isReadOnly.test(formValues, isReadOnly.dependencies);
            set(formValues, `${fullPath}.readOnly`, readonly);

            if (readonly) {
                const message = generateReadOnlyMessage(formValues, isReadOnly, permissionPath);
                set(formValues, `${fullPath}.message`, message);
            } else {
                set(formValues, `${fullPath}.message`, "");
            }
        }
    });
}

/** Return path for permissions on the category level */
const getCategoryPermissionFormPath = (categoryId: string, permission: string): string => {
    return `categories.${categoryId}.permissions.${permission}`;
};

/** This method is called in mapPropsToValues to setup the readOnly properties on a role permission object's controls since readOnly isn't calculated server side. */
export function checkDependenciesOnLoad(
    role: Role,
    hasAssignedUsers: boolean,
    builderFlags?: BuilderFlags,
): void {
    for (const [categoryKey, category] of Object.entries(role.categories)) {
        for (const [subCategoryKey, subCategory] of Object.entries(category.subCategories)) {
            for (const [permissionKey, permission] of Object.entries(subCategory.permissions)) {
                checkDependencies(
                    `categories.${categoryKey}.subCategories.${subCategoryKey}.permissions.${permissionKey}`,
                    permission.enabled,
                    role,
                    true,
                    hasAssignedUsers,
                    builderFlags,
                );
            }
        }
        for (const [customPermissionKey, customPermission] of Object.entries(
            category.permissions,
        )) {
            checkDependencies(
                `categories.${categoryKey}.permissions.${customPermissionKey}`,
                customPermission.enabled,
                role,
                true,
                false,
                builderFlags,
            );
        }
    }
}

/* This method applys the permissions from other roles to the current role, favoring enabled permissions. */
export function copyPermissionsFromRoles(currentRole: Role, copyFromRoles: Role[]): void {
    let hasUpdatedPermissions: boolean = false;
    for (const role of copyFromRoles) {
        for (const [categoryKey, category] of Object.entries(role.categories)) {
            for (const [subCategoryKey, subCategory] of Object.entries(category.subCategories)) {
                hasUpdatedPermissions = copySubpermissionsFromRole(
                    subCategory.permissions,
                    currentRole,
                    `categories.${categoryKey}.subCategories.${subCategoryKey}.permissions.{0}.enabled`,
                );
            }
            if (
                copySubpermissionsFromRole(
                    category.permissions,
                    currentRole,
                    `categories.${categoryKey}.permissions.{0}.enabled`,
                )
            ) {
                hasUpdatedPermissions = true;
            }
        }
    }
    checkDependenciesOnLoad(currentRole, false);
    if (hasUpdatedPermissions) {
        currentRole.key++;
    }
}

function copySubpermissionsFromRole(
    categoryPermissions: {
        [key: string]: Permission;
    },
    currentRole: Role,
    formatString: string,
): boolean {
    let hasUpdatedPermissions: boolean = false;
    for (const [permissionKey, permission] of Object.entries(categoryPermissions)) {
        if (permission.enabled && permission.canCopy !== false) {
            set(currentRole, formatString.replace("{0}", permissionKey), true);
            hasUpdatedPermissions = true;
        }
    }
    return hasUpdatedPermissions;
}

/** This method modifies the formValues object based on the dependencies between permissions */
export function checkDependencies(
    id: string,
    value: boolean,
    formValues: IRolePermissionFormValues,
    onLoad: boolean = false,
    hasAssignedUsers: boolean = false,
    builderFlags?: BuilderFlags,
): void {
    // filter out the unneeded form keys ("categories", "subcategories", "permissions")
    const idParts: string[] = id.split(".").filter((v, i) => {
        return i % 2 === 1;
    });
    const categoryId: string = idParts[0];
    const fieldId: string = idParts.splice(1, 2).join(".");
    switch (categoryId) {
        case "administrative":
            checkAdministrativeDependencies(fieldId, value, formValues, hasAssignedUsers, onLoad);
            break;
    }

    // Do a final check on the dependencies to make sure everything is in the correct state.
    verifyDependencyState(formValues, fieldId, builderFlags);
}

export function disableAdminPermsIfCurrentUser(
    entity: InternalUserRoleDetailsEntity,
    formValues: Role,
    currentUserId: number | undefined | null,
) {
    if (!currentUserId || !entity.assignedUsers[currentUserId]) {
        return;
    }

    // If a user is editing the permissions of their current role, admin permissions are read-only
    const adminView = getPermissionFormPath("administrative", "adminPerms", "view");
    const adminAdd = getPermissionFormPath("administrative", "adminPerms", "add");
    const adminUpdate = getPermissionFormPath("administrative", "adminPerms", "update");
    const adminDelete = getPermissionFormPath("administrative", "adminPerms", "delete");

    set(formValues, `${adminView}.readOnly`, true);
    set(formValues, `${adminAdd}.readOnly`, true);
    set(formValues, `${adminUpdate}.readOnly`, true);
    set(formValues, `${adminDelete}.readOnly`, true);
}

export function disablePermsThatCannotBeGranted(
    formValues: IRolePermissionFormValues,
    userInfo: UserInfo | undefined,
) {
    modifyManageAccountsReceivablePermissions(formValues, userInfo);
    modifyManageSubscriptionPaymentPermissions(formValues, userInfo);
}

function checkAdministrativeDependencies(
    id: string,
    value: boolean,
    formValues: IRolePermissionFormValues,
    hasAssignedUsers: boolean,
    onLoad: boolean,
): void {
    switch (id) {
        case "jobsitePerms.viewPresaleJobs":
        case "jobsitePerms.viewOpenJobs":
        case "jobsitePerms.viewWarrantyJobs":
        case "jobsitePerms.viewClosedJobs":
            if (hasAssignedUsers && onLoad) {
                modifyJobStatusPermissions(id, value, formValues);
            }
            break;
    }
}

function modifyJobStatusPermissions(
    jobStatusPermission: string,
    value: boolean,
    formValues: IRolePermissionFormValues,
): void {
    const subCategory = jobStatusPermission.split(".");
    const formPath = getPermissionFormPath("administrative", subCategory[0], subCategory[1]);

    if (value) {
        set(formValues, `${formPath}.readOnly`, true);
        set(
            formValues,
            `${formPath}.message`,
            "There are users associated with this role. Please remove users before changing this permission.",
        );
    } else {
        set(formValues, `${formPath}.message`, "");
    }
}

function modifyManageSubscriptionPaymentPermissions(
    formValues: IRolePermissionFormValues,
    userInfo: UserInfo | undefined,
): void {
    const formPath = getPermissionFormPath(
        "administrative",
        "adminPerms",
        "manageSubscriptionPayments",
    );
    const hasManageSubscriptionPermission = userInfo?.permissions?.hasPermission(
        "admin",
        "canManageSubscriptionPayments",
    );
    if (!hasManageSubscriptionPermission) {
        set(formValues, `${formPath}.readOnly`, true);
        set(
            formValues,
            `${formPath}.message`,
            "Only users with the org owner role can enable this permission.",
        );
    }
}

function modifyManageAccountsReceivablePermissions(
    formValues: IRolePermissionFormValues,
    userInfo: UserInfo | undefined,
): void {
    const formPath = getPermissionFormPath(
        "administrative",
        "adminPerms",
        "manageAccountsReceivablePayments",
    );
    const hasManageAccountsReceivablePermission = userInfo?.permissions?.hasPermission(
        "admin",
        "canManageAccountsReceivable",
    );
    if (!hasManageAccountsReceivablePermission) {
        set(formValues, `${formPath}.readOnly`, true);
        set(
            formValues,
            `${formPath}.message`,
            "Only users with the org owner role can enable this permission.",
        );
    }
}

export function generateReadOnlyMessage(
    formValues: IRolePermissionFormValues,
    dependencies: IPermissionDependencyTest | IPermissionDependencyTestSet,
    path: string,
): string {
    const { enabledDependencies, disabledDependencies } = analyzeReadOnlyDependencies(
        formValues,
        dependencies,
    );

    const formattedPath = formatPermissionName(path);

    // If we have enabled dependencies, generate message about needing to disable them
    if (enabledDependencies.length > 0) {
        if (enabledDependencies.length === 1) {
            const formattedPermissionName = formatPermissionName(enabledDependencies[0]);
            return `Cannot remove '${formattedPath}' permission when '${formattedPermissionName}' is selected.`;
        }

        const formattedNames = enabledDependencies.map(
            (dep: string) => `'${formatPermissionName(dep)}'`,
        );
        const lastPermission = formattedNames.pop();
        const permissionList = formattedNames.join(", ");
        const conjuction = dependencies.test === ifAny ? "or" : "and";

        return `Cannot remove '${formattedPath}' permission when ${permissionList} ${conjuction} ${lastPermission} are selected.`;
    }

    // If we have disabled dependencies, generate message about needing to enable them
    if (disabledDependencies.length > 0) {
        // For complex dependencies like ifNotAll with nested ifAny, provide more specific messaging
        if (dependencies.test === ifNotAll) {
            return generateNotAllDependencyMessage(formValues, dependencies, formattedPath);
        }

        if (disabledDependencies.length === 1) {
            const formattedPermissionName = formatPermissionName(disabledDependencies[0]);
            return `Role must have '${formattedPermissionName}' permission enabled to edit this permission.`;
        }

        const formattedNames = disabledDependencies.map(
            (dep: string) => `'${formatPermissionName(dep)}'`,
        );
        const lastPermission = formattedNames.pop();
        const permissionList = formattedNames.join(", ");

        return `Role must have ${permissionList} and ${lastPermission} permissions enabled to edit this permission.`;
    }

    return "This permission cannot be modified due to current permission settings.";
}

function generateNotAllDependencyMessage(
    formValues: IRolePermissionFormValues,
    dependencies: IPermissionDependencyTest | IPermissionDependencyTestSet,
    formattedPath: string,
): string {
    // For ifNotAll dependencies, we need to check which sub-conditions are failing
    const failingConditions: string[] = [];

    for (const dep of dependencies.dependencies) {
        if (typeof dep === "string") {
            // Handle simple string dependencies
            const isEnabled = get(formValues, getFullPath(dep), false);
            if (!isEnabled) {
                failingConditions.push(`'${formatPermissionName(dep)}'`);
            }
        } else if (typeof dep === "object" && dep.dependencies) {
            // Handle complex nested dependencies
            const conditionResult = dep.test(formValues, dep.dependencies);
            if (!conditionResult) {
                if (dep.test === ifAny) {
                    // For ifAny, we need at least one to be enabled
                    const anyPermissions = dep.dependencies
                        .filter((d: string) => typeof d === "string")
                        .map((d: string) => `'${formatPermissionName(d)}'`);

                    if (anyPermissions.length > 1) {
                        const lastPermission = anyPermissions.pop();
                        const permissionList = anyPermissions.join(", ");
                        failingConditions.push(`${permissionList} or ${lastPermission}`);
                    } else if (anyPermissions.length === 1) {
                        failingConditions.push(anyPermissions[0]);
                    }
                } else if (dep.test === ifAll) {
                    // For ifAll, all must be enabled
                    const allPermissions = dep.dependencies
                        .filter((d: string) => typeof d === "string")
                        .map((d: string) => `'${formatPermissionName(d)}'`);

                    if (allPermissions.length > 1) {
                        const lastPermission = allPermissions.pop();
                        const permissionList = allPermissions.join(", ");
                        failingConditions.push(`${permissionList} and ${lastPermission}`);
                    } else if (allPermissions.length === 1) {
                        failingConditions.push(allPermissions[0]);
                    }
                }
            }
        }
    }

    let conditionText = failingConditions.join("");
    let isPlural = false;
    if (failingConditions.length > 1) {
        const lastCondition = failingConditions.pop();
        const conditionList = failingConditions.join(", ");
        conditionText = `${conditionList} and ${lastCondition}`;
        isPlural = true;
    }
    if (failingConditions.length > 0) {
        return `Role must have ${conditionText} permission${
            isPlural ? "s" : ""
        } enabled to edit this permission.`;
    }

    return `Role must meet additional permission requirements to edit '${formattedPath}'.`;
}

function analyzeReadOnlyDependencies(
    formValues: IRolePermissionFormValues,
    dependency: IPermissionDependencyTest | IPermissionDependencyTestSet,
): { enabledDependencies: string[]; disabledDependencies: string[] } {
    const enabledPaths: string[] = [];
    const disabledPaths: string[] = [];

    function evaluateTestCondition(
        testCondition: IPermissionDependencyTest | IPermissionDependencyTestSet,
    ): boolean {
        return testCondition.test(formValues, testCondition.dependencies);
    }

    function analyzeCondition(
        condition: IPermissionDependencyTest | IPermissionDependencyTestSet,
        isRootCondition: boolean = false,
    ): void {
        const { test, dependencies } = condition;

        switch (test) {
            case ifAll:
                analyzeIfAll(dependencies);
                break;
            case ifAny:
                analyzeIfAny(dependencies, isRootCondition);
                break;
            case ifNotAll:
                analyzeIfNotAll(dependencies);
                break;
            case ifNone:
                analyzeIfNone(dependencies);
                break;
            default:
                throw new Error(`Unknown test type: ${test}`);
        }
    }

    function analyzeIfAll(dependencies: string[] | IPermissionDependencyTest[]) {
        // For ifAll in isReadOnly context, we want to show which dependencies are enabled
        // but only when ALL dependencies are enabled (i.e., when the ifAll condition is satisfied)
        const allEnabled = dependencies.every((dep) => {
            if (typeof dep === "string") {
                return get(formValues, getFullPath(dep), false);
            } else if (typeof dep === "object") {
                return evaluateTestCondition(dep);
            }
            return false;
        });

        if (allEnabled) {
            // All are enabled, so add them to enabledPaths to show in readonly message
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    enabledPaths.push(dep);
                } else if (typeof dep === "object") {
                    analyzeCondition(dep);
                }
            }
        } else {
            // Not all are enabled, so add the disabled ones to disabledPaths
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    const isEnabled = get(formValues, getFullPath(dep), false);
                    if (!isEnabled) {
                        disabledPaths.push(dep);
                    }
                } else if (typeof dep === "object" && !evaluateTestCondition(dep)) {
                    analyzeCondition(dep);
                }
            }
        }
    }

    function analyzeIfAny(
        dependencies: string[] | IPermissionDependencyTest[],
        isRootCondition: boolean,
    ) {
        // For ifAny, at least one dependency must be enabled
        const anyEnabled = dependencies.some((dep) => {
            if (typeof dep === "string") {
                return get(formValues, getFullPath(dep), false);
            } else if (typeof dep === "object") {
                return evaluateTestCondition(dep);
            }
            return false;
        });

        if (!anyEnabled) {
            // None are enabled, so all are required
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    disabledPaths.push(dep);
                } else if (typeof dep === "object") {
                    analyzeCondition(dep);
                }
            }
        } else if (isRootCondition) {
            // If this is the root condition and it's satisfied, find which ones are enabled
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    const isEnabled = get(formValues, getFullPath(dep), false);
                    if (isEnabled) {
                        enabledPaths.push(dep);
                    }
                } else if (typeof dep === "object" && evaluateTestCondition(dep)) {
                    analyzeCondition(dep);
                }
            }
        }
    }

    function analyzeIfNotAll(dependencies: string[] | IPermissionDependencyTest[]) {
        // For ifNotAll, not all dependencies should be enabled
        // This means at least one must be disabled for the condition to be true
        const allEnabled = dependencies.every((dep) => {
            if (typeof dep === "string") {
                return get(formValues, getFullPath(dep), false);
            } else if (typeof dep === "object") {
                return evaluateTestCondition(dep);
            }
            return false;
        });

        if (allEnabled) {
            // All are enabled, so we need to disable at least one
            // Find which ones are enabled to show in the message
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    const isEnabled = get(formValues, getFullPath(dep), false);
                    if (isEnabled) {
                        enabledPaths.push(dep);
                    }
                } else if (typeof dep === "object" && evaluateTestCondition(dep)) {
                    analyzeCondition(dep);
                }
            }
        } else {
            // Not all are enabled, so find which ones need to be enabled
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    const isEnabled = get(formValues, getFullPath(dep), false);
                    if (!isEnabled) {
                        disabledPaths.push(dep);
                    }
                } else if (typeof dep === "object" && !evaluateTestCondition(dep)) {
                    analyzeCondition(dep);
                }
            }
        }
    }

    function analyzeIfNone(dependencies: string[] | IPermissionDependencyTest[]) {
        // For ifNone, no dependencies should be enabled
        // The permission is read-only when NONE of the dependencies are enabled
        const noneEnabled = dependencies.every((dep) => {
            if (typeof dep === "string") {
                return !get(formValues, getFullPath(dep), false);
            } else if (typeof dep === "object") {
                return !evaluateTestCondition(dep);
            }
            return true;
        });

        if (noneEnabled) {
            // None are enabled, so add them to disabledPaths to show in readonly message
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    disabledPaths.push(dep);
                } else if (typeof dep === "object") {
                    analyzeCondition(dep);
                }
            }
        } else {
            // Some are enabled, so add the enabled ones to enabledPaths
            for (const dep of dependencies) {
                if (typeof dep === "string") {
                    const isEnabled = get(formValues, getFullPath(dep), false);
                    if (isEnabled) {
                        enabledPaths.push(dep);
                    }
                } else if (typeof dep === "object" && evaluateTestCondition(dep)) {
                    analyzeCondition(dep);
                }
            }
        }
    }

    analyzeCondition(dependency, true);

    return { enabledDependencies: enabledPaths, disabledDependencies: disabledPaths };
}
